import {
    AlertInput,
    IonAlert,
    IonButton,
    IonCheckbox,
    IonCol,
    IonIcon,
    IonLabel,
    IonRow,
    IonSelectOption,
    IonText,
    useIonToast,
} from '@ionic/react';
import { Trans, t } from '@lingui/macro';
import classNames from 'classnames';
import { FormikProps, useFormikContext } from 'formik';
import { chevronDownOutline, chevronUpOutline } from 'ionicons/icons';
import { useAtom } from 'jotai';
import React, { useCallback, useState } from 'react';

import { IOdooRoom } from '../interfaces/IOdooRoomsResponse';
import { DevicesUpdateFormValues } from '../pages/installation/wire/Devices';
import {
    accessTokenAtom,
    configLoadableAtom,
    deepomaticAtom,
    jobIdAtom,
    updateDBStateAtom,
} from '../store/Atoms';
import { updateDeviceRequest } from '../store/Requests';
import {
    ControlType,
    DeepomaticTaskGroups,
    DeepomaticTasksWithOCR,
} from '../types/Generic';
import { THomeAssetDataWithFWAndCalibration } from '../types/HomeAssetData';
import { ImageCategories } from '../types/ImageInfo';
import { MAX_IMAGE_NR } from '../utils/constants';
import { getDeepomaticTaskgroup } from '../utils/deepomatic';
import AssetTest from './assettest/AssetTest';
import DefaultSelect from './forms/defaultselect/DefaultSelect';
import LoadingButton from './loadingbutton/LoadingButton';
import PhotoUpload from './photoUpload/PhotoUpload';
import {
    TransServerMessage,
    getTransServerMessageString,
} from './translations/TransServerMessage';

// TODO: remove this and get the list from the API
const HEATER_MODES = [
    { label: 'Home', value: 'home' },
    { label: 'AUTO', value: 'auto' },
    { label: 'Driver', value: 'driver' },
    { label: 'Clock', value: 'clock' },
    { label: 'Sun', value: 'sun' },
    { label: 'Comfort', value: 'comfort' },
    { label: 'Manual', value: 'manual' },
    { label: 'Suitcase', value: 'suitcase' },
    { label: 'Heat', value: 'heat' },
    { label: 'ECO', value: 'eco' },
    { label: 'Moon', value: 'moon' },
    { label: 'Snowflake', value: 'snowflake' },
    { label: 'PROG', value: 'pilot_wire' },
    { label: 'Centralization', value: 'centralization' },
];

const MAX_HEATER_TEMPERATURE = 30;
const MIN_HEATER_TEMPERATURE = 18;

const HEATER_TEMPERATURES = Array(
    MAX_HEATER_TEMPERATURE - MIN_HEATER_TEMPERATURE + 1
)
    .fill({})
    .map((_, i) => {
        return {
            label: MAX_HEATER_TEMPERATURE - i,
            value: MAX_HEATER_TEMPERATURE - i,
        };
    });

interface IHeaterProps {
    room: IOdooRoom;
    device: THomeAssetDataWithFWAndCalibration;
    index: number;
    deepomaticWorkOrderId: string | false | undefined;
    formRef?: React.RefObject<FormikProps<DevicesUpdateFormValues>>;
}

const RoomHeater: React.FunctionComponent<IHeaterProps> = ({
    room,
    device,
    index,
    deepomaticWorkOrderId,
    formRef,
}) => {
    const {
        macAddress,
        filPilote,
        isNew,
        linkToInstructionManual,
        additionalInformation,
        requiredSettings,
    } = device;
    const {
        values,
        handleChange,
        handleBlur,
        setFieldValue,
        validateForm,
        errors,
        touched,
        setFieldTouched,
    } = useFormikContext<{
        devices: {
            id: number;
            filPilote: boolean;
            screen: boolean;
            thermostat: number;
            setting: string;
            dboxHeaterRelativeLocation: string;
            dboxAirFlowQuality: string;
            dboxCloseToHeater: string;
        }[];
    }>();
    const [presentToast] = useIonToast();
    const [jobId] = useAtom(jobIdAtom);
    const [accessToken] = useAtom(accessTokenAtom);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [, updateDBState] = useAtom(updateDBStateAtom);
    const [deepomatic] = useAtom(deepomaticAtom);
    const [value] = useAtom(configLoadableAtom);
    const [heaterInfoShown, showHeaterInfo] = useState(false);
    const [recomSettingsIsOpen, setRecomSettingsIsOpen] = useState(false);

    const settingName = `devices.${index}.setting`;
    // const settingError = getIn(errors, settingName);
    // const settingTouched = getIn(touched, settingName);

    const screenName = `devices.${index}.screen`;
    // const screenError = getIn(errors, screenName);
    // const screenTouched = getIn(touched, screenName);

    const thermostatName = `devices.${index}.thermostat`;
    const dboxHeaterRelativeLocationName = `devices.${index}.dboxHeaterRelativeLocation`;
    const dboxAirFlowQualityName = `devices.${index}.dboxAirFlowQuality`;
    const dboxCloseToHeaterName = `devices.${index}.dboxCloseToHeater`;

    const requiredSettingsPresent =
        requiredSettings && requiredSettings.length > 0;
    const displayRecomSettingsBtn = filPilote && requiredSettingsPresent;

    const deviceDboxHeaterRelativeLocationTypes =
        value.state === 'hasData'
            ? value.data.selects.deviceDboxHeaterRelativeLocationTypes
            : [];
    const deviceDboxAirFlowQualityTypes =
        value.state === 'hasData'
            ? value.data.selects.deviceDboxAirFlowQualityTypes
            : [];
    const deviceDboxCloseToHeaterTypes =
        value.state === 'hasData'
            ? value.data.selects.deviceDboxCloseToHeaterTypes
            : [];
    const fieldHasErrors = (fieldName: string): boolean => {
        if (errors && errors.devices) {
            const deviceErrors = errors.devices[index];
            if (deviceErrors && deviceErrors.hasOwnProperty(fieldName)) {
                return true;
            }
        }
        return false;
    };
    const fieldIsTouched = (fieldName: string): boolean => {
        if (touched && touched.devices) {
            const deviceTouched = touched.devices[index];
            if (deviceTouched && deviceTouched.hasOwnProperty(fieldName)) {
                // @ts-ignore
                return deviceTouched[fieldName];
            }
        }
        return false;
    };
    const getFieldErrorText = (fieldName: string): string => {
        if (errors && errors.devices) {
            const deviceErrors = errors.devices[index];
            if (deviceErrors && deviceErrors.hasOwnProperty(fieldName)) {
                // @ts-ignore
                return deviceErrors[fieldName];
            }
        }
        return '';
    };
    const getHeaterModeDisplayName = (mode: string) => {
        const searchResult = HEATER_MODES.find((m) => m.value === mode);
        if (searchResult) {
            return getTransServerMessageString(searchResult.label);
        }
        return mode;
    };

    const getRecommendedSettingsListValues = (): AlertInput[] => {
        return requiredSettings.map((r) => {
            return {
                id: r.id,
                type: 'radio',
                value: `${r.value[0]}, ${r.value[1]}`,
                label: `${getHeaterModeDisplayName(r.value[0] as string)}, ${r.value[1]}`,
            };
        });
    };

    const handleHeaterSettingSelection = async (
        setting: string
    ): Promise<void> => {
        let modeAndThermostat;
        if (setting) modeAndThermostat = setting.split(',');
        if (modeAndThermostat) {
            await setFieldValue(
                `devices.${index}.setting`,
                modeAndThermostat[0]
            );
            await setFieldValue(
                `devices.${index}.thermostat`,
                parseInt(modeAndThermostat[1])
            );
        }
    };

    const onSaveHeaterDetails = useCallback(async () => {
        await setFieldTouched(`devices.${index}.thermostat`);
        await setFieldTouched(`devices.${index}.setting`);
        setIsSubmitting(true);
        try {
            const deviceData = values.devices[index];
            const formValidated = await validateForm({
                devices: [deviceData],
            });
            const isValid = Object.keys(formValidated).length === 0;

            if (isValid) {
                const {
                    id,
                    setting,
                    screen,
                    thermostat,
                    dboxHeaterRelativeLocation,
                    dboxAirFlowQuality,
                    dboxCloseToHeater,
                } = deviceData;
                await updateDeviceRequest(accessToken, jobId, id, {
                    setting,
                    screen,
                    thermostat,
                    dboxHeaterRelativeLocation,
                    dboxAirFlowQuality,
                    dboxCloseToHeater,
                });
                await updateDBState();
            }
        } catch (e: any) {
            const response = e.response;
            const errorMessage = response.devices
                ? response.devices.reduce((acc: string, message: string) => {
                      return `${acc}${message}<br/>`;
                  }, '')
                : e.reason;
            presentToast({
                message: errorMessage,
                duration: 5000,
                color: 'danger',
                buttons: [
                    {
                        text: t`Dismiss`,
                        role: 'cancel',
                    },
                ],
            });
        }
        setIsSubmitting(false);
    }, [
        accessToken,
        index,
        jobId,
        presentToast,
        updateDBState,
        validateForm,
        values.devices,
        setFieldTouched,
    ]);

    const handleScreenCheckbox = useCallback(() => {
        setFieldValue(screenName, !values.devices[index].screen);
    }, [index, screenName, setFieldValue, values.devices]);

    return (
        <>
            <IonRow className="ion-margin-top">
                <IonLabel>{macAddress}</IonLabel>
            </IonRow>
            <IonRow className="ion-margin-top ion-spacer-bottom">
                <IonLabel>
                    <Trans>Wiring mode:</Trans>{' '}
                    {filPilote ? (
                        <Trans>Fil Pilote</Trans>
                    ) : (
                        <Trans>Relay</Trans>
                    )}
                </IonLabel>
            </IonRow>
            <AssetTest device={device} />
            <PhotoUpload
                className="ion-margin-top"
                imageCode={`${ImageCategories.heaterPhotoDigitalScreen}_${macAddress}`}
                maxNumber={MAX_IMAGE_NR}
                label={t`Buttons or digital screen`}
                mandatory={isNew}
                deepomaticWorkOrderId={deepomaticWorkOrderId}
                deepomaticTaskGroup={getDeepomaticTaskgroup(
                    macAddress,
                    DeepomaticTaskGroups.BUTTON_OR_SCREEN,
                    deepomatic
                )}
                deviceMacAddress={macAddress}
                controlType={
                    filPilote ? ControlType.PILOTE : ControlType.RELAIS
                }
                formRef={formRef}
                deepomaticTasksToFormNames={[
                    {
                        taskName:
                            DeepomaticTasksWithOCR.DIGITAL_SCREEN_TEMPERATURE,
                        fieldName: `devices.${index}.thermostat`,
                        fieldDisplayName: 'thermostat',
                    },
                ]}
            ></PhotoUpload>
            <PhotoUpload
                className="ion-margin-top"
                imageCode={`${ImageCategories.heaterDBOXWhole}_${device.macAddress}`}
                maxNumber={MAX_IMAGE_NR}
                label={t`DBOX + heater as a whole`}
                mandatory={
                    (room?.interventionReasonCodes &&
                        room.interventionReasonCodes.length > 0 &&
                        !room.interventionReasonCodes
                            .map((a) => a.code)
                            .includes('RIR_0010')) ||
                    device.isNew
                }
                deepomaticWorkOrderId={deepomaticWorkOrderId}
                deepomaticTaskGroup={getDeepomaticTaskgroup(
                    device.macAddress,
                    DeepomaticTaskGroups.DBOX_AND_HEATER,
                    deepomatic
                )}
                formRef={formRef}
                deepomaticTasksToFormNames={[
                    {
                        taskName:
                            DeepomaticTasksWithOCR.DBOX_RELATIVE_POSITION,
                        fieldName: `devices.${index}.dboxHeaterRelativeLocation`,
                        fieldDisplayName: 'D-Box location',
                    },
                ]}
                deviceMacAddress={device.macAddress}
                controlType={
                    device.filPilote ? ControlType.PILOTE : ControlType.RELAIS
                }
            ></PhotoUpload>
            {filPilote && (
                <IonRow className="ion-justify-content-between ion-align-items-center ion-margin-top">
                    <IonCol>
                        <IonLabel>
                            <Trans>Does it have a screen?</Trans>
                        </IonLabel>
                    </IonCol>
                    <IonCol size="auto">
                        <IonCheckbox
                            name={screenName}
                            checked={values.devices[index].screen}
                            onIonChange={handleScreenCheckbox}
                        ></IonCheckbox>
                    </IonCol>
                </IonRow>
            )}
            {displayRecomSettingsBtn && (
                <IonButton
                    size="small"
                    onClick={() => setRecomSettingsIsOpen(true)}
                >
                    <Trans>Rec. settings</Trans>
                </IonButton>
            )}
            <IonAlert
                isOpen={recomSettingsIsOpen}
                header={t`Recommended settings`}
                buttons={[
                    t`Cancel`,
                    {
                        text: t`Ok`,
                        handler: (data) => handleHeaterSettingSelection(data),
                    },
                ]}
                onDidDismiss={() => setRecomSettingsIsOpen(false)}
                inputs={getRecommendedSettingsListValues()}
            ></IonAlert>
            {filPilote && (
                <DefaultSelect
                    className={classNames({
                        'ion-invalid': fieldHasErrors('setting'),
                        'ion-touched': fieldIsTouched('setting'),
                    })}
                    name={settingName}
                    value={values.devices[index].setting}
                    label={t`Heater Mode`}
                    placeholder={t`Select a heater mode`}
                    onIonChange={handleChange}
                    onIonBlur={handleBlur}
                    errorText={getFieldErrorText('setting')}
                >
                    {HEATER_MODES.map((mode) => (
                        <IonSelectOption key={mode.value} value={mode.value}>
                            <TransServerMessage id={mode.label} />
                        </IonSelectOption>
                    ))}
                </DefaultSelect>
            )}
            {filPilote && (
                <DefaultSelect
                    className={classNames({
                        'ion-invalid': fieldHasErrors('thermostat'),
                        'ion-touched': fieldIsTouched('thermostat'),
                    })}
                    name={thermostatName}
                    value={values.devices[index].thermostat}
                    label={t`Heater Thermostat`}
                    placeholder={t`Select a temperature`}
                    onIonChange={handleChange}
                    onIonBlur={handleBlur}
                    errorText={getFieldErrorText('thermostat')}
                >
                    {HEATER_TEMPERATURES.map((t) => (
                        <IonSelectOption key={t.value} value={t.value}>
                            {t.label}
                        </IonSelectOption>
                    ))}
                </DefaultSelect>
            )}
            <DefaultSelect
                className={classNames({
                    'ion-invalid': fieldHasErrors(
                        'dboxHeaterRelativeLocation'
                    ),
                    'ion-touched': fieldIsTouched(
                        'dboxHeaterRelativeLocation'
                    ),
                })}
                name={dboxHeaterRelativeLocationName}
                value={values.devices[index].dboxHeaterRelativeLocation}
                label={t`D-Box location`}
                placeholder={t`Select an option`}
                onIonChange={handleChange}
                onIonBlur={handleBlur}
                errorText={getFieldErrorText('dboxHeaterRelativeLocation')}
            >
                {deviceDboxHeaterRelativeLocationTypes.map(
                    (deviceDboxHeaterRelativeLocationType) => (
                        <IonSelectOption
                            key={deviceDboxHeaterRelativeLocationType.value}
                            value={deviceDboxHeaterRelativeLocationType.value}
                        >
                            <TransServerMessage
                                id={deviceDboxHeaterRelativeLocationType.label}
                            />
                        </IonSelectOption>
                    )
                )}
            </DefaultSelect>
            <DefaultSelect
                className={classNames({
                    'ion-invalid': fieldHasErrors('dboxAirFlowQuality'),
                    'ion-touched': fieldIsTouched('dboxAirFlowQuality'),
                })}
                name={dboxAirFlowQualityName}
                value={values.devices[index].dboxAirFlowQuality}
                label={t`How far is the nearest wall/furniture from the D-Box?`}
                placeholder={t`Select an option`}
                onIonChange={handleChange}
                onIonBlur={handleBlur}
                helperText={
                    <Trans>
                        Does a solid object (furniture, wall, etc.) other than
                        the radiator restrict the airflow around the D-Box?
                        <br />• 0-5 cm: Yes;
                        <br />• 5-15 cm: Slightly;
                        <br />• +15 cm: No
                    </Trans>
                }
                errorText={getFieldErrorText('dboxAirFlowQuality')}
            >
                {deviceDboxAirFlowQualityTypes.map(
                    (deviceDboxAirFlowQualityType) => (
                        <IonSelectOption
                            key={deviceDboxAirFlowQualityType.value}
                            value={deviceDboxAirFlowQualityType.value}
                        >
                            <TransServerMessage
                                id={deviceDboxAirFlowQualityType.label}
                            />
                        </IonSelectOption>
                    )
                )}
            </DefaultSelect>
            <DefaultSelect
                className={classNames({
                    'ion-invalid': fieldHasErrors('dboxCloseToHeater'),
                    'ion-touched': fieldIsTouched('dboxCloseToHeater'),
                })}
                name={dboxCloseToHeaterName}
                value={values.devices[index].dboxCloseToHeater}
                label={t`Is the D-Box mounted close to the heater?`}
                placeholder={t`Select an option`}
                onIonChange={handleChange}
                onIonBlur={handleBlur}
                helperText={
                    <Trans>
                        • 0-2 cm: yes;
                        <br />• +2cm: no;
                    </Trans>
                }
                errorText={getFieldErrorText('dboxCloseToHeater')}
            >
                {deviceDboxCloseToHeaterTypes.map(
                    (deviceDboxCloseToHeaterType) => (
                        <IonSelectOption
                            key={deviceDboxCloseToHeaterType.value}
                            value={deviceDboxCloseToHeaterType.value}
                        >
                            <TransServerMessage
                                id={deviceDboxCloseToHeaterType.label}
                            />
                        </IonSelectOption>
                    )
                )}
            </DefaultSelect>
            {(additionalInformation || linkToInstructionManual) && (
                <div>
                    <IonRow className="ion-justify-content-between ion-align-items-center ion-margin-top">
                        <IonCol>
                            <IonLabel>
                                <Trans>Heater set-up info</Trans>
                            </IonLabel>
                        </IonCol>
                        {heaterInfoShown ? (
                            <IonIcon
                                onClick={() => showHeaterInfo(false)}
                                icon={chevronUpOutline}
                            ></IonIcon>
                        ) : (
                            <IonIcon
                                onClick={() => showHeaterInfo(true)}
                                icon={chevronDownOutline}
                            ></IonIcon>
                        )}
                    </IonRow>
                    {heaterInfoShown && (
                        <IonRow class={'ion-margin-top'}>
                            {additionalInformation && (
                                <IonText>{additionalInformation}</IonText>
                            )}
                            {linkToInstructionManual && (
                                <IonText>
                                    <Trans>
                                        Visit this{' '}
                                        <a
                                            target="_blank"
                                            rel="noreferrer"
                                            href={linkToInstructionManual}
                                        >
                                            link
                                        </a>{' '}
                                        to access the instructions manual
                                    </Trans>
                                </IonText>
                            )}
                        </IonRow>
                    )}
                </div>
            )}
            {filPilote && (
                <LoadingButton
                    className="ion-margin-top"
                    loading={isSubmitting}
                    onClick={onSaveHeaterDetails}
                >
                    <Trans>Save Heater details</Trans>
                </LoadingButton>
            )}
        </>
    );
};

export default RoomHeater;
